import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Calendar, ChevronDown, BarChart3, TrendingUp, TrendingDown, AlertTriangle, PlusCircle } from 'lucide-react';
import StatCard from '../shared/StatCard';
import { usePlayer } from '../../context/PlayerContext';
import { calculateRollingAverages } from '../../utils/performanceForecasting';
import { formatBattingStat, formatPitchingStat } from '../../utils/calculations';

// Helper function to format dates
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-dark-300 p-2 border border-gray-200 dark:border-dark-400 shadow-md rounded text-xs">
        <p className="font-semibold dark:text-darkText-primary">{payload[0].payload.fullDate}</p>
        <p className="text-blue-600 dark:text-blue-400">{`vs ${payload[0].payload.opponent}`}</p>
        <p className="dark:text-darkText-primary">{`${payload[0].name}: ${payload[0].value}`}</p>
        {payload.length > 1 && <p className="dark:text-darkText-primary">{`${payload[1].name}: ${payload[1].value}`}</p>}
        {payload[0].payload.notes && <p className="text-gray-600 dark:text-gray-400 mt-1">{payload[0].payload.notes}</p>}
      </div>
    );
  }
  return null;
};

const PerformanceTrends = ({ loading = false, hasStats = false }) => {
  const { playerData, activeSeason, viewingAllSeasons } = usePlayer();
  const [selectedMetric, setSelectedMetric] = useState('battingAvg');
  const [dateRange, setDateRange] = useState('lastTen');
  const [chartData, setChartData] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [compareMetric, setCompareMetric] = useState('obp');
  
  // Determine which stats to use (season or career)
  const battingStats = !viewingAllSeasons && activeSeason && playerData?.seasonStats?.battingStats
    ? playerData.seasonStats.battingStats
    : playerData?.battingStats;
    
  const pitchingStats = !viewingAllSeasons && activeSeason && playerData?.seasonStats?.pitchingStats
    ? playerData.seasonStats.pitchingStats
    : playerData?.pitchingStats;
  
  // Log the stats being used
  console.log('PerformanceTrends using stats:', {
    usingSeasonStats: !viewingAllSeasons && activeSeason && playerData?.seasonStats,
    activeSeason: activeSeason?.name,
    viewingAllSeasons,
    battingStats: {
      games: battingStats?.games || 0,
      atBats: battingStats?.atBats || 0,
      hits: battingStats?.hits || 0,
      average: battingStats?.average || 0
    }
  });
  // Generate trend data from pre-calculated trends or calculate on the client
  useEffect(() => {
    if (loading) return;
    
    // Reset chart data when season changes
    setChartData([]);
    
    // Check if we have enough stats to generate data
    const hasEnoughStats = playerData && playerData.recentGames && playerData.recentGames.length >= 3;
    
    if (!hasEnoughStats) {
      // If no stats for this season, set empty chart data
      setChartData([]);
      return;
    }
    
    const fetchPreCalculatedTrends = async () => {
      try {
        // Try to fetch pre-calculated trends from Firebase
        const { db } = await import('../../firebase/config');
        const { doc, getDoc } = await import('firebase/firestore');
        
        if (!playerData.uid || !playerData.id) {
          console.log('Missing player ID or user ID, calculating trends locally');
          calculateTrendsLocally();
          return;
        }
        
        const trendsRef = doc(db, 'users', playerData.uid, 'players', playerData.id, 'stats', 'performanceTrends');
        const trendsSnapshot = await getDoc(trendsRef);
        
        if (trendsSnapshot.exists()) {
          // Use pre-calculated trends from Firebase
          const trendsData = trendsSnapshot.data();
          
          // Use the appropriate rolling averages based on the date range
          let rollingAverages = [];
          if (dateRange === 'last7' || dateRange === 'lastTen') {
            rollingAverages = trendsData.rollingAverages5 || [];
          } else {
            rollingAverages = trendsData.rollingAverages10 || [];
          }
          
          // Filter by season if needed
          if (!viewingAllSeasons && activeSeason) {
            rollingAverages = rollingAverages.filter(avg => {
              // Find the corresponding game to check its season
              const game = playerData.recentGames.find(g => g.id === avg.gameId);
              return game && game.seasonId === activeSeason.id;
            });
          }
          
          // Convert rolling averages to chart data format
          const data = rollingAverages.map(avg => {
            // Format the date for display
            const date = new Date(avg.date);
            const formattedDate = formatDate(date);
            const fullDate = date.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            });
            
            // Find the corresponding game to get opponent info
            const game = playerData.recentGames.find(g => g.id === avg.gameId);
            
            return {
              date: formattedDate,
              fullDate,
              gameId: avg.gameId,
              opponent: game?.opponent || 'Unknown',
              result: game?.result || '',
              notes: game?.notes || '',
              seasonId: game?.seasonId || '',
              // Map the stats to our chart metrics
              battingAvg: avg.battingStats.average,
              obp: avg.battingStats.obp,
              slg: avg.battingStats.slg,
              ops: avg.battingStats.ops,
              era: avg.pitchingStats.era,
              whip: avg.pitchingStats.whip
            };
          });
          
          setChartData(data);
          console.log('Using pre-calculated performance trends from Firebase');
          return;
        }
        
        // If no pre-calculated trends, fall back to client-side calculation
        console.log('No pre-calculated trends found, calculating on client');
        calculateTrendsLocally();
      } catch (error) {
        console.error('Error fetching pre-calculated trends:', error);
        // Fall back to client-side calculation
        calculateTrendsLocally();
      }
    };
    
    const calculateTrendsLocally = () => {
      // Get the games for the current season
      let games = playerData.recentGames;
      
      // Filter games by season if viewing a specific season
      if (!viewingAllSeasons && activeSeason) {
        games = games.filter(game => game.seasonId === activeSeason.id);
      }
      
      // If we don't have enough games after filtering, return empty data
      if (games.length < 3) {
        setChartData([]);
        return;
      }
      
      // Calculate rolling averages using our utility function
      const rollingAverages = calculateRollingAverages(games, 5); // Use a 5-game rolling window
      
      // Convert rolling averages to chart data format
      const data = rollingAverages.map(avg => {
        // Format the date for display
        const date = new Date(avg.date);
        const formattedDate = formatDate(date);
        const fullDate = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        });
        
        // Find the corresponding game to get opponent info
        const game = games.find(g => g.id === avg.gameId);
        
        return {
          date: formattedDate,
          fullDate,
          gameId: avg.gameId,
          opponent: game?.opponent || 'Unknown',
          result: game?.result || '',
          notes: game?.notes || '',
          seasonId: game?.seasonId || '',
          // Map the stats to our chart metrics
          battingAvg: avg.battingStats.average,
          obp: avg.battingStats.obp,
          slg: avg.battingStats.slg,
          ops: avg.battingStats.ops,
          era: avg.pitchingStats.era,
          whip: avg.pitchingStats.whip
        };
      });
      
      setChartData(data);
    };
    
    // Start by trying to fetch pre-calculated trends
    fetchPreCalculatedTrends();
  }, [loading, playerData, activeSeason, viewingAllSeasons, dateRange]);
  
  // Filter data based on selected date range
  const filteredData = useMemo(() => {
    if (!chartData.length) return [];
    
    switch(dateRange) {
      case 'last7':
        return chartData.slice(-7);
      case 'last14':
        return chartData.slice(-14);
      case 'lastMonth':
        return chartData.slice(-30);
      case 'lastTen':
      default:
        return chartData.slice(-10);
    }
  }, [chartData, dateRange]);
  
  // Calculate average of last few games
  const calculateRecentAvg = (metric, games = 5) => {
    if (!chartData.length) return '0.000';

    const recentGames = chartData.slice(-games);
    const sum = recentGames.reduce((total, game) => total + (game[metric] || 0), 0);
    const avg = sum / recentGames.length;

    // Use appropriate formatter based on metric type
    if (metric === 'era' || metric === 'whip') {
      return formatPitchingStat(avg);
    }
    return formatBattingStat(avg);
  };
  
  // Calculate change trend (up or down)
  const calculateTrend = (metric, games = 5) => {
    if (chartData.length < games * 2) return 0;
    
    const recentGames = chartData.slice(-games);
    const previousGames = chartData.slice(-games * 2, -games);
    
    const recentAvg = recentGames.reduce((total, game) => total + (game[metric] || 0), 0) / recentGames.length;
    const previousAvg = previousGames.reduce((total, game) => total + (game[metric] || 0), 0) / previousGames.length;
    
    // For ERA and WHIP, lower is better, so we invert the trend
    const invertTrend = metric === 'era' || metric === 'whip';
    const percentChange = ((recentAvg - previousAvg) / previousAvg) * 100;
    
    return invertTrend ? -percentChange : percentChange;
  };
  
  // Get metric info
  const getMetricInfo = (metric) => {
    const metrics = {
      battingAvg: { label: 'Batting Average', color: '#3b82f6', id: 'battingAvg', icon: BarChart3 },
      obp: { label: 'On-Base %', color: '#10b981', id: 'obp', icon: BarChart3 },
      slg: { label: 'Slugging %', color: '#f59e0b', id: 'slg', icon: BarChart3 },
      ops: { label: 'OPS', color: '#8b5cf6', id: 'ops', icon: BarChart3 },
      era: { label: 'ERA', color: '#ef4444', id: 'era', icon: TrendingUp },
      whip: { label: 'WHIP', color: '#ec4899', id: 'whip', icon: TrendingUp }
    };
    
    return metrics[metric] || metrics.battingAvg;
  };
  
  const primaryMetric = getMetricInfo(selectedMetric);
  const secondaryMetric = getMetricInfo(compareMetric);
  
  // Get player career average for reference line
  const getCareerAverage = (metric) => {
    if (!playerData) return 0;
    
    // Log the stats being used for reference line
    console.log('PerformanceTrends reference line using stats:', {
      usingSeasonStats: !viewingAllSeasons && activeSeason && playerData?.seasonStats,
      activeSeason: activeSeason?.name,
      viewingAllSeasons,
      battingStats: {
        average: battingStats?.average || 0,
        obp: battingStats?.obp || 0,
        slg: battingStats?.slg || 0,
        ops: battingStats?.ops || 0
      }
    });
    
    switch(metric) {
      case 'battingAvg':
        return battingStats?.average || 0.250;
      case 'obp':
        return battingStats?.obp || 0.320;
      case 'slg':
        return battingStats?.slg || 0.400;
      case 'ops':
        return battingStats?.ops || 0.720;
      case 'era':
        return pitchingStats?.era || 4.00;
      case 'whip':
        return pitchingStats?.whip || 1.30;
      default:
        return 0;
    }
  };
  
  // Determine Y axis domain based on metric
  const getYAxisDomain = (metric) => {
    switch(metric) {
      case 'battingAvg':
        return [0, 0.5];
      case 'obp':
        return [0, 0.6];
      case 'slg':
        return [0, 0.8];
      case 'ops':
        return [0, 1.2];
      case 'era':
        return [0, 7];
      case 'whip':
        return [0, 2];
      default:
        return [0, 'auto'];
    }
  };
  
  const yAxisDomain = getYAxisDomain(selectedMetric);
  
  // Format tooltip value based on metric
  const formatValue = (value, metric) => {
    if (metric === 'era' || metric === 'whip') {
      return formatPitchingStat(value);
    }
    return formatBattingStat(value);
  };

  return (
    <StatCard>
      {/* Chart Controls */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <div className="flex items-center">
          <div className="relative">
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="pr-8 pl-3 py-1 bg-white dark:bg-dark-300 text-gray-800 dark:text-darkText-primary border border-gray-300 dark:border-dark-400 rounded-md text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="battingAvg">Batting Average</option>
              <option value="obp">On-Base %</option>
              <option value="slg">Slugging %</option>
              <option value="ops">OPS</option>
              <option value="era">ERA</option>
              <option value="whip">WHIP</option>
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-darkText-secondary pointer-events-none" />
          </div>
          
          <div className="ml-2 flex items-center">
            <input 
              type="checkbox" 
              id="compareToggle" 
              checked={showCompare} 
              onChange={() => setShowCompare(!showCompare)}
              className="mr-1"
              disabled={loading}
            />
            <label htmlFor="compareToggle" className="text-sm dark:text-darkText-primary">Compare</label>
          </div>
          
          {showCompare && (
            <div className="relative ml-2">
              <select
                value={compareMetric}
                onChange={(e) => setCompareMetric(e.target.value)}
                className="pr-8 pl-3 py-1 bg-white dark:bg-dark-300 text-gray-800 dark:text-darkText-primary border border-gray-300 dark:border-dark-400 rounded-md text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
                <option value="obp">On-Base %</option>
                <option value="slg">Slugging %</option>
                <option value="ops">OPS</option>
                <option value="battingAvg">Batting Average</option>
                <option value="era">ERA</option>
                <option value="whip">WHIP</option>
              </select>
              <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-darkText-secondary pointer-events-none" />
            </div>
          )}
        </div>
        
        <div className="flex items-center">
          <div className="relative flex items-center">
            <Calendar size={14} className="mr-1 text-gray-500" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="pr-8 pl-3 py-1 bg-white dark:bg-dark-300 text-gray-800 dark:text-darkText-primary border border-gray-300 dark:border-dark-400 rounded-md text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="lastTen">Last 10 Games</option>
              <option value="last7">Last 7 Games</option>
              <option value="last14">Last 14 Games</option>
              <option value="lastMonth">Last Month</option>
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-darkText-secondary pointer-events-none" />
          </div>
        </div>
      </div>
      
      {/* Loading State */}
      {loading ? (
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200 rounded w-full mb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-2 bg-gray-200 rounded w-full"></div>
            </div>
            <div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-2 bg-gray-200 rounded w-full"></div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* The Chart */}
          {/* Check if we have enough data to show the chart */}
          {battingStats && battingStats.games >= 3 ? (
            <div className="h-64 w-full mb-4">
              {filteredData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={filteredData}
                    margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
                    />
                    <YAxis 
                      domain={yAxisDomain}
                      tick={{ fontSize: 10 }} 
                      tickFormatter={(value) => formatValue(value, selectedMetric)}
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb', strokeWidth: 1 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend verticalAlign="top" height={36} />
                    
                    {/* Career average reference line */}
                    <ReferenceLine 
                      y={getCareerAverage(selectedMetric)} 
                      stroke="#9ca3af" 
                      strokeDasharray="3 3"
                      label={{ 
                        value: 'Career Avg', 
                        position: 'insideBottomRight',
                        fontSize: 10, 
                        fill: '#9ca3af' 
                      }}
                    />
                    
                    {/* Primary Metric Line */}
                    <Line
                      type="monotone"
                      name={primaryMetric.label}
                      dataKey={selectedMetric}
                      stroke={primaryMetric.color}
                      strokeWidth={2}
                      dot={{ r: 3, strokeWidth: 1 }}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                      animationDuration={500}
                    />
                    
                    {/* Secondary Metric Line (if compare is enabled) */}
                    {showCompare && (
                      <Line
                        type="monotone"
                        name={secondaryMetric.label}
                        dataKey={compareMetric}
                        stroke={secondaryMetric.color}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ r: 3, strokeWidth: 0 }}
                        activeDot={{ r: 6, strokeWidth: 0 }}
                        animationDuration={800}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="flex flex-col items-center text-gray-500">
                    <AlertTriangle size={24} className="mb-2" />
                    <p>No data available for the selected period</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 text-center">
              <AlertTriangle size={24} className="mx-auto mb-2 text-gray-400" />
              <h4 className="font-medium text-gray-700 dark:text-darkText-primary mb-1">
                No performance data available
              </h4>
              <p className="text-sm text-gray-500 dark:text-darkText-secondary mb-4">
                {activeSeason 
                  ? `Add game stats to ${activeSeason.name} to see performance trends.`
                  : 'Select a season with game data to see performance trends.'}
              </p>
              <button 
                className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm leading-4 font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => {/* Navigate to add game */}}
              >
                <PlusCircle size={16} className="mr-1" />
                Add Game Stats
              </button>
            </div>
          )}
          
          {/* Performance Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium">Last 5 Games {primaryMetric.label}</p>
                <p className="text-sm font-bold">{calculateRecentAvg(selectedMetric, 5)}</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-1 overflow-hidden">
                <div 
                  className={`${
                    selectedMetric === 'era' || selectedMetric === 'whip' 
                      ? 'bg-red-500' 
                      : 'bg-green-500'
                  } h-2 rounded-full`} 
                  style={{ 
                    width: `${Math.min(Math.max(parseFloat(calculateRecentAvg(selectedMetric)) / 
                      (selectedMetric === 'era' 
                        ? 7 
                        : selectedMetric === 'whip' 
                          ? 2
                          : 0.5) * 100, 5), 100)}%` 
                  }}
                ></div>
              </div>
              <div className="flex justify-end mt-1">
                <div className="text-xs flex items-center">
                  {calculateTrend(selectedMetric) > 0 ? (
                    <span className="text-green-600 flex items-center">
                      <TrendingUp size={12} className="mr-1" />
                      {Math.abs(calculateTrend(selectedMetric)).toFixed(1)}%
                    </span>
                  ) : calculateTrend(selectedMetric) < 0 ? (
                    <span className="text-red-600 flex items-center">
                      <TrendingDown size={12} className="mr-1" />
                      {Math.abs(calculateTrend(selectedMetric)).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-gray-500">No change</span>
                  )}
                </div>
              </div>
            </div>
            
            {showCompare && (
              <div>
                <div className="flex justify-between items-center">
                  <p className="text-sm font-medium">Last 5 Games {secondaryMetric.label}</p>
                  <p className="text-sm font-bold">{calculateRecentAvg(compareMetric, 5)}</p>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1 overflow-hidden">
                  <div 
                    className={`${
                      compareMetric === 'era' || compareMetric === 'whip' 
                        ? 'bg-red-500' 
                        : 'bg-green-500'
                    } h-2 rounded-full`} 
                    style={{ 
                      width: `${Math.min(Math.max(parseFloat(calculateRecentAvg(compareMetric)) / 
                        (compareMetric === 'era' 
                          ? 7 
                          : compareMetric === 'whip' 
                            ? 2
                            : 0.5) * 100, 5), 100)}%` 
                    }}
                  ></div>
                </div>
                <div className="flex justify-end mt-1">
                  <div className="text-xs flex items-center">
                    {calculateTrend(compareMetric) > 0 ? (
                      <span className="text-green-600 flex items-center">
                        <TrendingUp size={12} className="mr-1" />
                        {Math.abs(calculateTrend(compareMetric)).toFixed(1)}%
                      </span>
                    ) : calculateTrend(compareMetric) < 0 ? (
                      <span className="text-red-600 flex items-center">
                        <TrendingDown size={12} className="mr-1" />
                        {Math.abs(calculateTrend(compareMetric)).toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-gray-500">No change</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </StatCard>
  );
};

// PropTypes definition
PerformanceTrends.propTypes = {
  loading: PropTypes.bool,
  hasStats: PropTypes.bool
};

export default PerformanceTrends;
